/* istanbul ignore file: https://clarifai.atlassian.net/browse/LT-2756 */
import { either } from 'fp-ts';
import { types } from 'mst-effect';
import { ReactLeft, UI_STATES } from 'utils/uiStates/uiStates';
import { applySnapshot, Instance, onSnapshot, SnapshotIn } from 'mobx-state-tree';
import { withRunInAction } from 'utils/withRunInAction';
import { nanoid } from 'nanoid';
import { pipe } from 'utils/fp';
import { PredUiItem, PredUiRegionItem } from 'utils/cfUiItems/predictions';
import { indexBy, isEmpty } from 'rambda';
import { isUiRegionItem } from 'utils/cfUiItems/guards';
import { extractInputsFromMetadata } from 'utils/predictions/extractInputsFromMetadata';
import { TSupportedPredictionCategories } from '../types';
import { viewModelOutputList } from './views';

export const DEFAULT_APP_SAMPLE_MS = 1000;

export type PredictionListMSTLeft<TEntity> = {
  title: string;
  reason: string;
  errorResponse?:
    | ({ status?: CF.API.Status } & Error)
    | (TEntity extends CF.API.Models.Model ? CF.API.ModelOutputs.ModelOutputsResponse : CF.API.WorkflowOutputs.WorkflowOutputsResponse);
};

type SavedPrediction = {
  input: CF.API.ModelOutputs.Input;
  data: CF.API.TOutputData;
};

const FrozenCarouselListMST = types.array(types.frozen<CF.API.Inputs.Input>());
export type TFrozenCarouselListMST = Instance<typeof FrozenCarouselListMST>;

/** state related to the UI */

/**
 * Determines where the focus behavior is applied, and to what extent:
 * * `'canvas'` -> User interaction is occuring on canvas, so we highlight the sidebar element.
 * * `'sidebar-region'` -> User interaction is occuring on a specific region, so we'll highlight that specific region.
 * * `'sidebar-concept'` -> User interaction is occuring on a specific concept, so we'll highlight all regions that
 * have that concept.
 * * `'none'` -> No User interaction is occuring.
 */
export type RegionFocusKind = 'canvas' | 'sidebar-region' | 'sidebar-concept' | 'none';

const ViewStateMST = types
  .model({
    isLoggedIn: types.optional(types.boolean, false),
    selectedVersionId: types.maybe(types.string),
    selectedWfNodeVersionId: types.maybe(types.string),
    selectedDeploymentId: types.maybe(types.string),
    currentSecond: types.optional(types.number, 0),
    minThreshold: types.optional(types.number, 0),

    /**
     * The key of the region that is being focused, if any.
     */
    focusedRegion: types.optional(types.string, ''),
    /**
     * Determines where the focus behavior is applied, and to what extent:
     * * `'canvas'` -> User interaction is occuring on canvas, so we highlight the sidebar element.
     * * `'sidebar-region'` -> User interaction is occuring on a specific region, so we'll highlight that specific region.
     * * `'sidebar-concept'` -> User interaction is occuring on a specific concept, so we'll highlight all regions that
     * have that concept.
     * * `'none'` -> No User interaction is occuring.
     */
    focusKind: types.optional(types.enumeration<RegionFocusKind>(['canvas', 'sidebar-region', 'sidebar-concept', 'none']), 'none'),
  })
  .actions((self) => ({
    setCurrentSecond: (frame: number, fps: number) => {
      self.currentSecond = Math.floor(frame / fps);
    },
    setMinThreshold: (num: number) => {
      self.minThreshold = Math.max(0, num);
    },
    setVersionId: (id: string | undefined) => {
      self.selectedVersionId = id;
    },
    setDeploymentId: (id: string | undefined) => {
      self.selectedDeploymentId = id;
    },
    setSelectedWfNodeVersionId: (id: string | undefined) => {
      self.selectedWfNodeVersionId = id;
    },
    setFocusedRegion: (focusKind: 'canvas' | 'sidebar-region' | 'sidebar-concept', regionKey: string) => {
      self.focusedRegion = regionKey;
      self.focusKind = focusKind;
    },
    clearFocusedRegion: () => {
      self.focusedRegion = '';
      self.focusKind = 'none';
    },
  }));

const SearchMST = types
  .model({
    term: types.maybe(types.string),
  })
  .actions((self) => ({
    setTerm: (key: string) => {
      self.term = key;
    },
  }))
  .named('Search');

const MediaMST = types
  .model({
    /** media items to power prediction carousel  */
    currentInputId: types.optional(types.string, ''),
    // preset list of images can be set using actions
    presetContent: FrozenCarouselListMST,
    // user uploaded content, i.e. media they add to test out predictions on the fly
    userUploadedContent: FrozenCarouselListMST,
  })
  .views((self) => ({
    get inputList() {
      return self.presetContent.concat(self.userUploadedContent);
    },
  }))
  .views((self) => ({
    get currentInput(): CF.API.Inputs.Input | null {
      const { currentInputId } = self;
      const foundInput = self.inputList.find((i) => i.id === currentInputId);

      if (!foundInput) return self.inputList.length ? self.inputList[0] : null;

      const inputMetadata = {
        ...(foundInput.data.metadata ? { metadata: foundInput.data.metadata } : {}),
      };

      if ('text' in foundInput.data && foundInput.data.text) {
        return {
          ...foundInput,
          data: {
            ...inputMetadata,
            text: foundInput.data.text,
          },
        };
      }

      if ('image' in foundInput.data && foundInput.data.image) {
        return {
          ...foundInput,
          data: {
            ...inputMetadata,
            image: foundInput.data.image,
          },
        };
      }

      if ('video' in foundInput.data && foundInput.data.video) {
        return {
          ...foundInput,
          data: {
            ...inputMetadata,
            video: foundInput.data.video,
          },
        };
      }

      if ('audio' in foundInput.data && foundInput.data.audio) {
        return {
          ...foundInput,
          data: {
            ...inputMetadata,
            audio: foundInput.data.audio,
          },
        };
      }

      return null;
    },
  }))
  .views((self) => ({
    get currentInputType(): 'image' | 'video' | 'text' {
      return 'image' in (self.currentInput?.data || {}) ? 'image' : 'video' in (self.currentInput?.data || {}) ? 'video' : 'text';
    },
  }))
  .actions((self) => ({
    addContent(data: Record<string, CF.API.Inputs.RawOrUrl | CF.API.Inputs.Base64OrUrl | CF.API.Metadata>) {
      // we can choose to limit the max existing custom inputs
      // if (self.userUploadedContent.length > 4) {
      //   self.userUploadedContent.shift();
      // }
      const newId = nanoid(5);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      self.userUploadedContent.push({ id: newId, data: data as unknown as any } as any);
      self.currentInputId = newId;
      return newId;
    },

    setPresetContent(presetContent: SnapshotIn<typeof FrozenCarouselListMST>) {
      applySnapshot(self.presetContent, presetContent);
    },

    setPresetContentFromMetadata(metadata: CF.API.Metadata) {
      const presetContent = extractInputsFromMetadata(metadata);
      self.presetContent.replace(presetContent);
    },

    setCurrentInput(id: string) {
      self.currentInputId = id;
    },
  }))
  .named('PredictionMedia');

// base state type for driving canvas prediction views with Clarifai WFs/Models
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const createBasePredictionStoreMST = <TEntity extends CF.API.Workflows.Workflow | CF.API.Models.Model>() =>
  // TEntity generic is used to predictably branch the `predict` action to handle both WFs and Models
  types
    .compose(
      withRunInAction(),
      types.model({
        /* Child nodes */
        media: types.optional(MediaMST, {}),
        search: types.optional(SearchMST, { term: '' }),
        viewState: types.optional(ViewStateMST, {}),

        /* Core state */
        renderingCategory: types.optional(
          types.enumeration<TSupportedPredictionCategories>(Object.values(TSupportedPredictionCategories)),
          TSupportedPredictionCategories.Initialising,
        ),
        // set the workflow/model; make sure to pass the entity as dependency to useMSTModel because we want the store to be
        // initiated afresh when the model/workflow changes instead of lingering state from previous entity
        entity:
          types.frozen<
            TEntity extends CF.API.Workflows.Workflow
              ? { type: 'workflow'; data: CF.API.Workflows.Workflow }
              : { type: 'model'; data: CF.API.Models.Model }
          >(),

        // sampleMs of the application
        appSampleMs: types.optional(types.number, DEFAULT_APP_SAMPLE_MS),

        /**
         * stores API response of predictions; frozen because predictions are not mutable or interactive unlike annotations
         * this is not goung to be used directly for powering UIs because Clarifai UIs are build on a different UI-oriented type
         * we will be converting this data in a `view` for the UIs to consume.
         */
        outputE: types.optional(
          types.frozen<either.Either<ReactLeft<PredictionListMSTLeft<TEntity>>, CF.API.ModelOutputs.ModelOutput[]>>(),
          either.left({ type: UI_STATES.idle }),
        ),

        /** is entity capable of running predictions?  */
        hasTrained: types.optional(types.boolean, false),
      }),
    )
    .actions((self) => ({
      setLoading: () => {
        self.outputE = either.left({ type: UI_STATES.loading });
      },
      setHasTrained(value: boolean) {
        self.hasTrained = value;
      },
    }))
    .views((self) => ({
      get maybeSelectedWfNodeOutput() {
        return either.isRight(self.outputE)
          ? self.outputE.right.find((item) => item.model?.model_version.id === self.viewState.selectedWfNodeVersionId)
          : undefined;
      },
    }))
    .views((self) => ({
      /** Output Grouping By Frame */
      get byFrame() {
        const emptyObj = {} as {
          [x: number]: {
            data: either.Either<
              ReactLeft,
              {
                items: PredUiItem[];
              }
            >;
            id: string;
            frame_info: {
              index: number;
              time: number;
            };
          };
        };
        const { outputE } = self;
        return pipe(
          outputE,
          either.map((outputArray) => {
            if (self.entity.type === 'model') {
              return outputArray[0];
            }
            return self.maybeSelectedWfNodeOutput;
          }),
          either.chain((x) => {
            return !x ? either.left({ type: UI_STATES.empty } as ReactLeft) : either.right(x);
          }),
          either.map((output) => {
            if ('frames' in output.data && output.data.frames) {
              const transformed = output.data.frames.filter((x) => !isEmpty(x.data)).map((i) => ({ ...i, data: viewModelOutputList(i.data) }));
              return indexBy((frame) => frame.frame_info.index, transformed);
            }
            return emptyObj;
          }),
          either.getOrElse(() => emptyObj),
        );
      },
    }))
    /** Output List View */
    .views((self) => {
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      const transformOutput = (
        outputE: either.Either<ReactLeft<PredictionListMSTLeft<TEntity>>, CF.API.ModelOutputs.ModelOutput[] | SavedPrediction[]>,
      ) =>
        pipe(
          outputE,
          either.map((outputArray) => {
            if (self.entity.type === 'model') {
              return outputArray[0];
            }
            return self.maybeSelectedWfNodeOutput;
          }),
          either.chain((x) => {
            return !x ? either.left({ type: UI_STATES.empty } as ReactLeft) : either.right(x);
          }),
          either.chain((output) => {
            if (self.media.currentInputType === 'image') {
              return viewModelOutputList(output.data);
            } else if (self.media.currentInputType === 'video') {
              return self.byFrame[self.viewState.currentSecond]?.data || either.left({ type: UI_STATES.empty });
            } else if (self.media.currentInputType === 'text') {
              return viewModelOutputList(output.data);
            }
            return either.left({ type: UI_STATES.invalid });
          }),
          either.map(({ items }) => {
            return {
              items: pipe(items, makeThresholdFilter(self.viewState.minThreshold), makeSearch(self.search.term || '')) as PredUiItem[],
            };
          }),
          either.chain((x) => (!x.items.length ? either.left({ type: UI_STATES.empty }) : either.right(x))),
        );

      return {
        get list() {
          return transformOutput(self.outputE);
        },
        /** Public cached prediction view */
        get publicList() {
          const inputId = self.media.currentInputId;
          const currentOutput =
            'presets' in self.entity.data && self.entity.data.presets && Array.isArray(self.entity.data.presets.outputs)
              ? (self.entity.data.presets as { outputs: SavedPrediction[] }).outputs.find((i) => i.input.id === inputId)
              : undefined;

          const outputE = currentOutput
            ? either.right([currentOutput])
            : (either.left({ type: UI_STATES.idle, title: '', reason: '' }) as either.Left<ReactLeft<PredictionListMSTLeft<TEntity>>>);

          return transformOutput(outputE);
        },
      };
    })
    .views((self) => ({
      get isPredictionsLoading() {
        return pipe(
          self.outputE,
          either.match(
            () => either.isLeft(self.outputE) && self.outputE.left.type === UI_STATES.loading,
            () => false,
          ),
        );
      },
    }))
    .views((self) => ({
      /** list for rendering in sidebar */
      get uiListE() {
        return self.viewState.isLoggedIn ? self.list : self.publicList;
      },
    }))
    .views((self) => ({
      /** canvas list, ensures we don't send non-region data */
      get regionListE() {
        return pipe(
          self.uiListE,
          either.chain((list) => {
            return isUiRegionItem(list.items[0])
              ? (either.right(list) as either.Either<ReactLeft, { items: PredUiRegionItem[] }>)
              : either.left({ type: UI_STATES.empty } as ReactLeft);
          }),
        );
      },
    }))
    .actions((self) => ({
      afterCreate: () => {
        // if no currentinput set it to the first input
        onSnapshot(self, () => {
          if (!self.media.currentInputId && self.media.inputList.length) {
            self.media.setCurrentInput(self.media.inputList[0]?.id);
          }
        });
      },
      setLastEligibleNode() {
        if (self.entity.type === 'workflow') {
          const eligibleNodesVersionIds = self.entity.data.nodes?.filter((i) => !i.suppress_output).map((i) => i.model.model_version.id);
          if (eligibleNodesVersionIds?.length) {
            self.viewState.setSelectedWfNodeVersionId(eligibleNodesVersionIds[eligibleNodesVersionIds.length - 1]);
          }
        }
      },
      // Please read arch docs to add async predict functionality in your own module.
      // predict: () => {...},
    }))
    .named('PredictionStore');

function makeThresholdFilter(thresholdMin: number): (items: PredUiItem[]) => PredUiItem[] {
  return (items) =>
    items.filter((i) => (typeof i.prediction.score === 'number' ? (i.prediction?.score as number) >= thresholdMin : true)) as PredUiItem[];
}

function makeSearch(searchTerm: string): (items: PredUiItem[]) => PredUiItem[] {
  return (items) =>
    searchTerm
      ? items.filter((i) => {
          const itemHash = getItemHash(i)?.toLowerCase();
          // if no itemhash; the item is of a type that doesn't contain a concept
          return itemHash ? itemHash.includes(searchTerm) : true;
        })
      : items;
}

// eslint-disable-next-line consistent-return
function getItemHash(item: PredUiItem): string | undefined {
  if ('concept' in item) {
    return `${item.concept?.name}${item.concept?.value}`;
  } else if ('concepts' in item && item.concepts) {
    return `${item.concepts[0]?.name}${item.concepts[0]?.value}`;
  } else if ('color' in item && item.color) {
    return `${item.color.raw_hex}${item.color.w3c.name}`;
  } else if ('text' in item) {
    return item.text?.raw;
  }
}

export type TBasePredictionType = ReturnType<typeof createBasePredictionStoreMST>;
export type TIBasePredictionMST = Instance<TBasePredictionType>;
